/**
 * 放送地点まるごとプレビュー (通し聴き) の進行状況ダイアログ。
 *
 * - 「この放送地点を通しで試聴」は編集モーダルの一番下にあり、押した後に増える表示
 *   (カラオケ文面 / 進捗 / エラー) がスクロール領域の外に出て気づきにくかった。
 *   そのため、試聴中は編集モーダルの上にモーダルを重ねて画面中央に表示する。
 * - 見た目は `NoticeDialog` に揃える (既存の `.modal-backdrop` / `.modal` 系 CSS を流用)。
 * - 開閉は呼び出し側が決める。再生中 or エラー残りのときだけ描画される想定:
 *   - 正常終了 / 停止: 呼び出し側の状態が戻り、自動で閉じる。
 *   - エラー / 一部スキップ警告: 残して「閉じる」で閉じる (見落とし防止)。
 * - 背景クリックでは閉じない。再生中は Esc で停止、エラー表示中は Esc で閉じる。
 * - z-index は地図ポップアップ (1100) より前面、`CopyDialog` (1200) より背面の 1150。
 */
import { useEffect } from 'react';

import type { KaraokeState } from '../hooks/useKaraokePlayback';

import { KaraokeText } from './KaraokeText';

export interface PointPreviewDialogProps {
  /** 生成 + 連続再生中か (true の間は「停止」ボタンを出す) */
  playing: boolean;
  /** 「いま何をしているか」の進捗テキスト */
  progress: string | null;
  /** いま鳴っている要素の文面と進捗 (カラオケ表示) */
  karaoke: KaraokeState | null;
  /** 失敗 / 一部スキップのメッセージ */
  error: string | null;
  /** 再生の停止 */
  onStop: () => void;
  /** ダイアログを閉じる (エラー表示の消去) */
  onClose: () => void;
}

export function PointPreviewDialog(props: PointPreviewDialogProps): React.JSX.Element {
  const { playing, progress, karaoke, error, onStop, onClose } = props;

  // Esc: 再生中は停止、それ以外 (エラー表示中) は閉じる
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      if (playing) onStop();
      else onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [playing, onStop, onClose]);

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="通し試聴"
      data-testid="point-preview-dialog"
      // 背景クリックでは閉じない (onClick 未設定)。
      style={{ zIndex: 1150 }}
    >
      <div className="modal modal--single-column" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, fontSize: 15 }}>🔊 この放送地点を通しで試聴</h2>
        </div>
        <div className="modal-body modal-body--single-column">
          <div className="form-pane">
            {progress ? (
              <div className="hint" data-testid="point-preview-progress" aria-live="polite">
                {progress}
              </div>
            ) : null}
            {karaoke ? (
              <KaraokeText
                text={karaoke.text}
                progress={karaoke.progress}
                label={karaoke.label}
                testId="point-preview-karaoke"
              />
            ) : null}
            {error ? (
              <div
                className="error-message"
                role="alert"
                data-testid="point-preview-error"
                style={{ marginTop: 4 }}
              >
                {error}
              </div>
            ) : null}
          </div>
        </div>
        <div className="modal-footer">
          {playing ? (
            <button type="button" data-testid="point-preview-stop" onClick={onStop} autoFocus>
              ■ 試聴を停止
            </button>
          ) : (
            <button
              type="button"
              data-testid="point-preview-dialog-close"
              className="primary"
              onClick={onClose}
              autoFocus
            >
              閉じる
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
